import * as React from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import type { RecentWins, RecentWinSale } from "@/types/affiliate-dashboard";
import type { StatValue } from "@/types/dashboard";
import { formatCurrency } from "@/utils/currency";
import { formatNumber } from "@/utils/formatters";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { relativeTime, shortDate } from "./helpers";

const columns: ColumnDef<RecentWinSale>[] = [
  {
    accessorKey: "orderNumber",
    header: "Order",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap font-medium tabular-nums text-foreground">#{row.original.orderNumber}</span>
    ),
  },
  {
    accessorKey: "amount",
    header: "Basket",
    meta: { width: "w-28" },
    cell: ({ row }) => <span className="tabular-nums text-foreground">{formatCurrency(row.original.amount)}</span>,
  },
  {
    accessorKey: "commission",
    header: "You earned",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="font-medium tabular-nums text-foreground">{formatCurrency(row.original.commission)}</span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "When",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground" title={new Date(row.original.createdAt).toLocaleString()}>
        {relativeTime(row.original.createdAt)}
      </span>
    ),
  },
];

/** Personal bests as the four figures behind the "Stats" flip. */
function bestsToStats(records: RecentWins["records"]): StatValue[] | undefined {
  const { biggestOrder, bestDay, bestMonth } = records;
  if (!biggestOrder && !bestDay && !bestMonth) return undefined;
  const stats: StatValue[] = [];
  if (biggestOrder) {
    stats.push({
      label: "Biggest order",
      value: biggestOrder.commission,
      kind: "currency",
      note: `${formatCurrency(biggestOrder.amount)} basket, ${shortDate(biggestOrder.date)}`,
    });
  }
  if (bestDay) {
    stats.push({
      label: "Best day",
      value: bestDay.commission,
      kind: "currency",
      note: `${formatNumber(bestDay.orders)} order${bestDay.orders === 1 ? "" : "s"} on ${shortDate(`${bestDay.date}T00:00:00`)}`,
    });
  }
  if (bestMonth) {
    stats.push({
      label: "Best month",
      value: bestMonth.commission,
      kind: "currency",
      note: new Date(`${bestMonth.month}-01T00:00:00`).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      }),
    });
  }
  return stats;
}

/** Fills the row beside the goal and payout cards and scrolls inside. The
    "Stats" flip shows the affiliate's personal bests. */
export function SalesCard({ data, isLoading }: { data: RecentWins | undefined; isLoading: boolean }) {
  const stats = React.useMemo(() => (data ? bestsToStats(data.records) : undefined), [data]);
  const rows = data?.recent ?? [];

  return (
    <DashboardCard id="dashboard-sales-card" title="Latest sales" fill stats={stats} bodyClassName="px-3 pb-3">
      {!isLoading && rows.length === 0 ? (
        <div className="flex min-h-[10rem] flex-1 items-center justify-center text-sm text-muted-foreground lg:min-h-0">
          No sales yet
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          isLoading={isLoading}
          skeletonRows={5}
          getRowId={(row) => row.id}
          emptyText="No sales yet"
          minTableWidth="min-w-[24rem]"
          maxHeight="max-h-[30rem] lg:max-h-full"
          className="lg:h-full"
        />
      )}
    </DashboardCard>
  );
}
